import type { TournamentDivision } from "./api";

export type TournamentDivisionDraft = {
  name: string;
  gender: string;
  play_mode: "singles" | "doubles";
  /** Series por equipos (Mini Copa): la categoría se inscribe por parejas y se juega en líneas. */
  team_play: boolean;
  age_min: string;
  age_max: string;
  level_label: string;
  registration_cost: string;
  max_participants: string;
  /** Cierre de inscripciones propio (AAAA-MM-DD); vacío = el del torneo. */
  registration_deadline?: string;
};

export const emptyDivisionDraft: TournamentDivisionDraft = {
  name: "",
  gender: "",
  play_mode: "singles",
  team_play: false,
  age_min: "",
  age_max: "",
  level_label: "",
  registration_cost: "",
  max_participants: "",
  registration_deadline: "",
};

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

function parseIsoDate(iso: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** «30 nov» (fecha de cierre de inscripciones). */
export function shortDateEs(iso: string | null | undefined): string {
  const date = parseIsoDate(iso);
  return date ? `${date.getDate()} ${MESES_CORTOS[date.getMonth()]}` : "";
}

/** «martes 25 nov». */
export function weekdayDateEs(iso: string | null | undefined): string {
  const date = parseIsoDate(iso);
  return date ? `${DIAS[date.getDay()]} ${shortDateEs(iso)}` : "";
}

/** Cuántas categorías traen cierre propio. */
export function divisionsWithOwnDeadline(divisions: Array<Pick<TournamentDivision, "registration_deadline">>): number {
  return divisions.filter((division) => Boolean(division.registration_deadline)).length;
}

/** Modalidad visible de una categoría: singles, dobles o por equipos
 * (Eduardo 2026-09-14: se elige desde Datos al crear la categoría). Por
 * equipos = parejas inscritas como equipo + series con líneas. */
export type DivisionModality = "singles" | "doubles" | "teams";

export const DIVISION_MODALITY_OPTIONS: Array<{ value: DivisionModality; label: string }> = [
  { value: "singles", label: "Singles" },
  { value: "doubles", label: "Dobles" },
  { value: "teams", label: "Por equipos (parejas · series de 2 singles + 1 dobles)" },
];

export function divisionModality(draft: Pick<TournamentDivisionDraft, "play_mode" | "team_play">): DivisionModality {
  if (draft.team_play) return "teams";
  return draft.play_mode === "doubles" ? "doubles" : "singles";
}

export function applyDivisionModality<T extends Pick<TournamentDivisionDraft, "play_mode" | "team_play">>(draft: T, modality: DivisionModality): T {
  if (modality === "teams") return { ...draft, play_mode: "doubles", team_play: true };
  return { ...draft, play_mode: modality, team_play: false };
}

export function divisionIsTeamPlay(division: Pick<TournamentDivision, "format_config_json">): boolean {
  const block = division.format_config_json?.team_play;
  return Boolean(block && typeof block === "object" && (block as { enabled?: unknown }).enabled === true);
}

export function divisionInputFromDraft(draft: TournamentDivisionDraft, sortOrder = 0) {
  const name = draft.name.trim();
  return {
    name,
    gender: draft.gender || null,
    play_mode: draft.team_play ? ("doubles" as const) : draft.play_mode,
    team_play: draft.team_play ? { enabled: true } : null,
    age_min: draft.age_min ? Number(draft.age_min) : null,
    age_max: draft.age_max ? Number(draft.age_max) : null,
    level_label: draft.level_label.trim() || null,
    registration_cost: draft.registration_cost.trim() || null,
    max_participants: draft.max_participants ? Number(draft.max_participants) : null,
    registration_deadline: draft.registration_deadline || null,
    sort_order: sortOrder,
    is_active: true,
  };
}

const DIVISION_COLOR_PALETTE = [
  "#22c55e", // verde
  "#38bdf8", // azul
  "#f97316", // naranja
  "#a78bfa", // morado
  "#f43f5e", // rosa/rojo
  "#eab308", // amarillo
  "#14b8a6", // teal
  "#fb7185", // coral
  "#60a5fa", // celeste
  "#c084fc", // violeta
];

function divisionColorIndex(division: Pick<TournamentDivision, "id" | "name">): number {
  if (Number.isFinite(division.id) && division.id > 0) {
    return Math.abs(division.id) % DIVISION_COLOR_PALETTE.length;
  }
  let hash = 0;
  for (let index = 0; index < division.name.length; index += 1) {
    hash = (hash * 31 + division.name.charCodeAt(index)) >>> 0;
  }
  return hash % DIVISION_COLOR_PALETTE.length;
}

export function divisionAccentColor(division: Pick<TournamentDivision, "id" | "name">): string {
  return DIVISION_COLOR_PALETTE[divisionColorIndex(division)];
}

export function divisionAccentStyle(division: Pick<TournamentDivision, "id" | "name">): Record<string, string> {
  const color = divisionAccentColor(division);
  return {
    "--division-accent": color,
    "--division-accent-soft": `${color}2b`,
    "--division-accent-faint": `${color}14`,
    "--division-accent-ring": `${color}55`,
  };
}

export function divisionDetailsLabel(
  division: Pick<TournamentDivision, "play_mode" | "gender" | "age_min" | "age_max" | "level_label" | "registration_cost"> & Partial<Pick<TournamentDivision, "format_config_json">>,
): string {
  const parts = [
    divisionIsTeamPlay({ format_config_json: division.format_config_json ?? null })
      ? "Por equipos (parejas)"
      : division.play_mode === "doubles" ? "Dobles" : "Singles",
  ];
  if (division.gender) {
    parts.push(division.gender === "male" ? "Varonil" : division.gender === "female" ? "Femenil" : "Mixto");
  }
  if (division.age_min !== null || division.age_max !== null) {
    const min = division.age_min ?? "inicio";
    const max = division.age_max ?? "+";
    parts.push(`Nac. ${min}–${max}`);
  }
  if (division.level_label) parts.push(division.level_label);
  if (division.registration_cost) parts.push(division.registration_cost);
  return parts.join(" · ");
}

export function yearFromTournamentDate(startDate?: string | null): number {
  const parsed = startDate ? Number(startDate.slice(0, 4)) : NaN;
  return Number.isFinite(parsed) && parsed > 1900 ? parsed : new Date().getFullYear();
}

export function divisionBirthYearRange(
  division: { age_min?: number | null; age_max?: number | null },
  tournamentYear: number,
): { from: number | null; to: number | null } | null {
  if (division.age_min == null && division.age_max == null) return null;
  const hasLegacyAgeValue =
    (division.age_min != null && division.age_min < 1900) ||
    (division.age_max != null && division.age_max < 1900);
  if (hasLegacyAgeValue) {
    return {
      from: division.age_max != null ? tournamentYear - division.age_max : null,
      to: division.age_min != null ? tournamentYear - division.age_min : null,
    };
  }
  return {
    from: division.age_min ?? null,
    to: division.age_max ?? null,
  };
}

export function divisionBirthYearLabel(
  division: { age_min?: number | null; age_max?: number | null },
  tournamentYear: number,
): string | null {
  const range = divisionBirthYearRange(division, tournamentYear);
  if (!range) return null;
  if (range.from !== null && range.to !== null) return `Nacidos ${range.from}–${range.to}`;
  if (range.from !== null) return `Nacidos en ${range.from} o después`;
  if (range.to !== null) return `Nacidos en ${range.to} o antes`;
  return null;
}

export function divisionToConvocatoriaLine(division: Pick<TournamentDivision, "name" | "play_mode" | "gender" | "age_min" | "age_max" | "level_label" | "registration_cost">): string {
  const details = divisionDetailsLabel(division);
  return details ? `${division.name} — ${details}` : division.name;
}

export function divisionsToConvocatoriaText(divisions: Array<Pick<TournamentDivision, "name" | "play_mode" | "gender" | "age_min" | "age_max" | "level_label" | "registration_cost" | "is_active">>): string {
  const active = divisions.filter((division) => division.is_active !== false);
  return active.map(divisionToConvocatoriaLine).join("\n");
}

/* Captura de edades (Eduardo 2026-09-27): en Los Encinos "7 y menores"
 * quedó con solo «Nacidos hasta 2018» → aceptaba a los de 8+ y rechazaba a
 * los chicos. Lectura en vivo del rango y aviso de rango invertido. */

function capturedBirthYear(value: string | null | undefined): number | null {
  const text = (value ?? "").trim();
  if (!text) return null;
  const year = Number(text);
  return Number.isInteger(year) && year >= 1900 && year <= 2100 ? year : null;
}

/** "Acepta nacidos en 2018 o antes: 8 años o más en 2026." (edad por año
 * calendario del torneo, la misma regla que valida el formulario). */
export function birthYearsPreview(
  ageMin: string | null | undefined,
  ageMax: string | null | undefined,
  tournamentYear: number,
): string | null {
  const from = capturedBirthYear(ageMin);
  const to = capturedBirthYear(ageMax);
  if (from === null && to === null) return null;
  const ageOf = (birthYear: number) => tournamentYear - birthYear;
  const years = (age: number) => (age === 1 ? "1 año" : `${age} años`);
  if (from !== null && to !== null) {
    if (from > to) return null; // el formulario ya avisa que el rango está al revés
    if (from === to) return `Acepta nacidos en ${from}: ${years(ageOf(from))} en ${tournamentYear}.`;
    return `Acepta nacidos ${from}–${to}: de ${ageOf(to)} a ${years(ageOf(from))} en ${tournamentYear}.`;
  }
  if (from !== null) return `Acepta nacidos en ${from} o después: ${years(ageOf(from))} o menos en ${tournamentYear}.`;
  return `Acepta nacidos en ${to} o antes: ${years(ageOf(to as number))} o más en ${tournamentYear}.`;
}

/** Aviso cuando el nombre dice «y menores» pero solo se capturó «hasta» (o
 * «y mayores» con solo «desde»): casi siempre es el rango invertido. */
export function birthYearsWarning(
  name: string | null | undefined,
  ageMin: string | null | undefined,
  ageMax: string | null | undefined,
): string | null {
  const from = capturedBirthYear(ageMin);
  const to = capturedBirthYear(ageMax);
  const text = (name ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const youth = /y\s*menores|menores\s+de|\bsub[\s-]?\d+|\bu[\s-]?\d{1,2}\b/.test(text);
  const senior = /y\s*mayores|mayores\s+de|\d+\s*\+|\+\s*\d+|veteran|senior|master/.test(text);
  if (youth && from === null && to !== null) {
    return "Ojo: en una categoría «y menores» el año va en «Nacidos desde» (los mayores del grupo). Con solo «Nacidos hasta» quedan fuera los más chicos.";
  }
  if (senior && to === null && from !== null) {
    return "Ojo: en una categoría «y mayores» el año va en «Nacidos hasta» (los más jóvenes del grupo). Con solo «Nacidos desde» quedan fuera los mayores.";
  }
  return null;
}

/** Categoría que elige la barra de arriba («Categoría seleccionada») cuando
 * la dirección no trae ?division: la primera ACTIVA por nombre. Eduardo
 * 2026-10-05 (revisión del torneo 69): Grupos usaba la primera por orden
 * del servidor y, mientras cargaba la barra, mostraba otra categoría y luego
 * saltaba a la de arriba. Una sola regla para las dos. */
export function firstActiveDivisionByName<T extends Pick<TournamentDivision, "name" | "is_active">>(
  divisions: readonly T[],
): T | null {
  const active = divisions.filter((division) => division.is_active !== false);
  active.sort((left, right) => left.name.localeCompare(right.name, "es", { sensitivity: "base" }));
  return active[0] ?? null;
}

